// A supplier's statement sheet (board SupStatementSheet; docs/plans/supplier-statement.md):
// the customer's statement sheet for the other side. Pick a period; the PDF is made on this
// device at once, then shared (phones) or downloaded (desks). The CSV and Print need no wait.
import type { SupplierRow } from '@ims/data'
import { Button, Modal, Table, TD, TH, cx, useToast } from '@ims/ui'
import { CheckCircle2, Download, FileCsv, FilePdf, Printer, Share2 } from '@ims/ui/icons'
import { useEffect, useMemo, useState } from 'react'
import { DateInput } from '../components.tsx'
import { errorMessage, useSettings } from '../hooks.ts'
import { downloadBlob } from '../lib/download.ts'
import { dateShort, money } from '../lib/format.ts'
import { finePointer } from '../lib/pointer.ts'
import { calendarOf, periodSlug, rangePresets } from '../lib/reports.ts'
import { canShareFile, shareFile } from '../lib/share.ts'
import { statementFileName } from '../lib/statement.ts'
import {
  buildSupplierStatement,
  supplierStanding,
  supplierRowDetail,
  supplierRowWhat,
  supplierStatementCsv,
  type SupplierLedger,
  type SupplierStatementRow,
} from '../lib/supplier-statement.ts'
import { printDocument } from '../print.tsx'
import type { SupplierStatementPdf } from '../supplier-statement-pdf.ts'
import type { StatementPeriod } from './statement-sheet.tsx'

const PERIODS: StatementPeriod[] = ['month', 'last-month', 'year', 'last-year']
const SHORT: Record<Exclude<StatementPeriod, 'custom'>, string> = {
  month: 'This month',
  'last-month': 'Last month',
  year: 'This fiscal year',
  'last-year': 'Last fiscal year',
}

const sharesFiles =
  typeof File !== 'undefined' &&
  canShareFile(new File([''], 'statement.pdf', { type: 'application/pdf' }))

function kb(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function SupplierStatementSheet({
  supplier,
  ledger,
  today,
  onClose,
}: {
  supplier: SupplierRow
  ledger: SupplierLedger
  today: string
  onClose: () => void
}) {
  const settings = useSettings()
  const toast = useToast()
  const presets = rangePresets(today, settings)
  const [period, setPeriod] = useState<StatementPeriod>('year')
  const [custom, setCustom] = useState(() => {
    const year = presets.find((p) => p.key === 'year') ?? presets[0]!
    return { from: year.from, to: year.to }
  })
  const range =
    period === 'custom' ? custom : (presets.find((p) => p.key === period) ?? presets[0]!)
  const statement = useMemo(
    () => buildSupplierStatement(ledger, { from: range.from, to: range.to }),
    [ledger, range.from, range.to],
  )
  const standing = useMemo(() => supplierStanding(ledger, today), [ledger, today])
  const name = statementFileName(supplier.name, periodSlug(period, range, settings))

  const key = `${range.from}|${range.to}|${statement.rows.length}|${statement.closingPaisa}`
  const [made, setMade] = useState<{ key: string; pdf: SupplierStatementPdf } | null>(null)
  const [progress, setProgress] = useState<{ key: string; done: number; total: number } | null>(
    null,
  )
  const [failed, setFailed] = useState<{ key: string; message: string } | null>(null)
  useEffect(() => {
    const stop = new AbortController()
    void import('../supplier-statement-pdf.ts')
      .then(({ makeSupplierStatementPdf }) =>
        makeSupplierStatementPdf(
          { statement, standing, supplier, settings, today },
          { signal: stop.signal, onPage: (done, total) => setProgress({ key, done, total }) },
        ),
      )
      .then((pdf) => setMade({ key, pdf }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setFailed({ key, message: errorMessage(error) })
      })
    return () => stop.abort()
    // `key` names everything the PDF is made from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, settings])
  const pdf = made?.key === key ? made.pdf : null
  const step = progress?.key === key ? progress : null
  const error = failed?.key === key ? failed.message : null

  const file = () => new File([pdf!.blob], `${name}.pdf`, { type: 'application/pdf' })
  const share = async () => {
    try {
      const result = await shareFile(file())
      if (result === 'downloaded') toast.success('PDF downloaded')
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  const download = () => {
    downloadBlob(file(), `${name}.pdf`)
    toast.success('PDF downloaded')
  }
  const csv = () => {
    downloadBlob(
      new Blob([supplierStatementCsv(statement)], { type: 'text/csv;charset=utf-8' }),
      `${name}.csv`,
    )
    toast.success('CSV downloaded')
  }
  // The same pages as the PDF, one to a sheet of paper.
  const print = () =>
    pdf &&
    printDocument(
      <div>
        {pdf.images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            style={{ display: 'block', width: '100%', breakAfter: 'page' }}
          />
        ))}
      </div>,
      'a4',
      name,
    )

  const shareFirst = sharesFiles && !finePointer
  const cal = calendarOf(settings)
  const pdfButton = (main: boolean) =>
    main && shareFirst ? (
      <Button
        icon={<Share2 className="size-4" />}
        size="lg"
        className="w-full"
        disabled={!pdf}
        onClick={() => void share()}
      >
        {pdf ? 'Share PDF' : 'Preparing PDF…'}
      </Button>
    ) : (
      <Button
        icon={<Download className="size-4" />}
        variant={main ? 'primary' : 'secondary'}
        className={cx(!finePointer && 'w-full')}
        disabled={!pdf}
        onClick={download}
      >
        Download PDF
      </Button>
    )

  return (
    <Modal
      open
      onClose={onClose}
      title={`Statement with ${supplier.name}`}
      size="md"
      footer={
        finePointer ? (
          <div className="flex w-full flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              icon={<Printer className="size-4" />}
              disabled={!pdf}
              onClick={print}
            >
              Print instead
            </Button>
            <span className="ml-auto flex flex-wrap gap-2">
              <Button variant="secondary" icon={<FileCsv className="size-4" />} onClick={csv}>
                Download CSV
              </Button>
              {sharesFiles && (
                <Button
                  variant="secondary"
                  icon={<Share2 className="size-4" />}
                  disabled={!pdf}
                  onClick={() => void share()}
                >
                  Share
                </Button>
              )}
              {pdfButton(true)}
            </span>
          </div>
        ) : (
          <div className="grid w-full gap-2">
            {pdfButton(true)}
            <div className="grid grid-cols-2 gap-2">
              {shareFirst ? pdfButton(false) : null}
              <Button
                variant="secondary"
                icon={<FileCsv className="size-4" />}
                className={cx('w-full', !shareFirst && 'col-span-2')}
                onClick={csv}
              >
                Download CSV
              </Button>
            </div>
            <Button
              variant="ghost"
              icon={<Printer className="size-4" />}
              disabled={!pdf}
              onClick={print}
            >
              Print instead
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-sm font-semibold text-muted" id="supplier-statement-period">
            Period
          </p>
          <div
            role="group"
            aria-labelledby="supplier-statement-period"
            className="flex flex-wrap gap-1.5"
          >
            {[...PERIODS, 'custom' as const].map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={period === p}
                onClick={() => {
                  if (p === 'custom' && period !== 'custom')
                    setCustom({ from: range.from, to: range.to })
                  setPeriod(p)
                }}
                className={cx(
                  'h-8 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap transition-colors',
                  period === p
                    ? 'border-primary/30 bg-primary-light text-primary'
                    : 'border-border bg-surface text-muted hover:text-ink',
                )}
              >
                {p === 'custom' ? 'Choose dates' : SHORT[p]}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <DateInput
                label="From"
                calendar={cal}
                value={custom.from}
                onChange={(iso) => setCustom((c) => ({ ...c, from: iso }))}
              />
              <DateInput
                label="To"
                calendar={cal}
                value={custom.to}
                onChange={(iso) => setCustom((c) => ({ ...c, to: iso }))}
              />
            </div>
          )}
        </div>

        <div className="flex items-start gap-4 rounded-xl border border-border bg-neutral-wash p-3">
          {pdf ? (
            <img
              src={pdf.preview}
              alt="Page 1 of the statement"
              className="w-28 shrink-0 rounded-lg shadow-card ring-1 ring-border"
            />
          ) : (
            <div className="flex w-28 shrink-0 flex-col gap-2 rounded-lg bg-surface p-3 shadow-card ring-1 ring-border">
              {[60, 40, 90, 90, 90, 75, 90, 55].map((w, i) => (
                <span
                  key={i}
                  aria-hidden
                  className="block h-1.5 animate-pulse rounded-full bg-soft"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
          )}
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-semibold break-words text-ink">{name}.pdf</p>
            {error ? (
              <p className="mt-1 text-danger">{error}</p>
            ) : pdf ? (
              <>
                <p className="text-muted">
                  {pdf.pages} page{pdf.pages === 1 ? '' : 's'} · {kb(pdf.blob.size)}
                </p>
                <p className="mt-2 flex items-center gap-1.5 font-semibold text-success">
                  <CheckCircle2 className="size-4" /> Ready
                </p>
              </>
            ) : (
              <>
                <p className="text-muted">Making the pages…</p>
                <div
                  role="progressbar"
                  aria-label="Making the PDF"
                  aria-valuemin={0}
                  aria-valuemax={step?.total ?? 1}
                  aria-valuenow={step?.done ?? 0}
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-soft"
                >
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${step ? (step.done / step.total) * 100 : 8}%` }}
                  />
                </div>
              </>
            )}
            <p className="mt-2 text-muted">
              {dateShort(range.from, settings)} – {dateShort(range.to, settings)}
              <br />
              You owe {money(statement.closingPaisa, settings)}
            </p>
          </div>
          <FilePdf className="hidden size-5 shrink-0 text-buy sm:block" aria-hidden />
        </div>
        <p className="text-xs text-subtle">
          Made on this device. Nothing is uploaded; the file goes only where you send it.
        </p>
      </div>
    </Modal>
  )
}

const DOT: Record<SupplierStatementRow['kind'], string> = {
  bill: 'bg-neutral',
  payment: 'bg-success',
  return: 'bg-insight',
}

/**
 * The supplier page's Statement tab (board SupStatementPage): this fiscal year in the order
 * things happened, with the running balance of what you owe.
 */
export function SupplierStatementTable({
  ledger,
  today,
}: {
  ledger: SupplierLedger
  today: string
}) {
  const settings = useSettings()
  const year = rangePresets(today, settings).find((p) => p.key === 'year')
  const from = year?.from
  const to = year?.to
  const statement = useMemo(
    () => (from && to ? buildSupplierStatement(ledger, { from, to }) : null),
    [ledger, from, to],
  )
  if (!statement || !year) return null
  const amount = (p: number) => money(p, settings)
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-4 py-2.5 text-sm text-muted">
        <span className="font-semibold text-ink">{year.label}</span>
        {(
          [
            ['Bill', 'bill'],
            ['Payment', 'payment'],
            ['Return', 'return'],
          ] as const
        ).map(([label, kind]) => (
          <span key={kind} className="inline-flex items-center gap-1.5">
            <span className={cx('size-1.5 rounded-full', DOT[kind])} />
            {label}
          </span>
        ))}
        <span className="ml-auto max-sm:hidden">Oldest first.</span>
      </div>
      <Table stack>
        <thead>
          <tr>
            <TH>Date</TH>
            <TH>What</TH>
            <TH align="right">Billed</TH>
            <TH align="right">Paid or returned</TH>
            <TH align="right">Balance</TH>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-neutral-wash">
            <TD className="stack-start stack-r2 text-muted max-sm:text-xs">
              {dateShort(statement.from, settings)}
            </TD>
            <TD className="stack-main stack-r1 font-semibold">Balance before this period</TD>
            <TD className="max-sm:hidden" />
            <TD className="max-sm:hidden" />
            <TD align="right" className="stack-end stack-r1 font-semibold">
              {amount(statement.openingPaisa)}
            </TD>
          </tr>
          {statement.rows.map((r) => {
            const detail = supplierRowDetail(r, (iso) => dateShort(iso, settings))
            return (
              <tr key={r.id}>
                <TD className="stack-start stack-r2 text-muted max-sm:text-xs">
                  {dateShort(r.date, settings)}
                </TD>
                <TD className="stack-main stack-r1">
                  <span className="inline-flex flex-wrap items-baseline gap-x-2">
                    <span
                      className={cx('size-1.5 shrink-0 self-center rounded-full', DOT[r.kind])}
                    />
                    <b className="font-semibold">
                      {supplierRowWhat(r)}
                      {r.kind !== 'payment' && r.number ? ` ${r.number}` : ''}
                    </b>
                    {detail && <span className="text-sm text-subtle">{detail}</span>}
                  </span>
                </TD>
                <TD align="right" className="stack-mid stack-r2 max-sm:text-xs">
                  {r.billedPaisa ? amount(r.billedPaisa) : ''}
                </TD>
                <TD align="right" className="stack-mid stack-r2 text-success max-sm:text-xs">
                  {r.paidPaisa ? amount(r.paidPaisa) : ''}
                </TD>
                <TD align="right" className="stack-end stack-r1 font-semibold">
                  {amount(r.balancePaisa)}
                </TD>
              </tr>
            )
          })}
          <tr className="bg-warning-light font-semibold text-warning">
            <TD className="stack-start stack-r2 max-sm:text-xs">
              {dateShort(statement.to, settings)}
            </TD>
            <TD className="stack-main stack-r1">You owe</TD>
            <TD align="right" className="max-sm:hidden">
              {amount(statement.billedPaisa)}
            </TD>
            <TD align="right" className="max-sm:hidden">
              {amount(statement.paidPaisa)}
            </TD>
            <TD align="right" className="stack-end stack-r1 font-bold">
              {amount(statement.closingPaisa)}
            </TD>
          </tr>
        </tbody>
      </Table>
    </div>
  )
}
